<template>
  <div class="configuration">
    <GenericScimConnectorHeader
      :displayInputQuery="!customApiFeatureFlag"
    />
    <IdmUsersImportFilterSection
      v-if="customApiFeatureFlag"
    />
    <div :class="$style.sectionRoot">
      <div
        ref="stickyHeader"
        :class="$style.stickyHeader"
      >
        <GenericScimConnectorConfigurationHeader
          :enableTestConnectionButton="areCredentialsValid"
        />
        <hr :class="$style.hr">
      </div>

      <GenericScimConnectorValidation />
      <OAuthConfigurationSection
        v-if="isMethodOAuth2"
        ref="oauthConfigRef"
        :class="$style.scrollMargin"
      />

      <div
        v-if="showCustom"
        ref="customProtocolConfigRef"
        :class="$style.scrollMargin"
      >
        <EndpointConfigurationSection />
        <GenericScimConnectorErrors />
        <CustomApiAttributeMapping />
      </div>
      <div v-else>
        <GenericScimConnectorGroups />
        <ScimConnectorAttributes
          v-if="scimIsTestConnectionPassed"
          :class="$style.sectionGeneralMargin"
        />
        <GenericScimConnectorErrors />
      </div>
    </div>
    <IdmOauthConfigReloadModal
      :showOauthConfigReloadModal="showOauthConfigReloadModal"
      @close="$emit('closeConfigReloadModal')"
    />
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { authenticationMethods, scimConnectionTypes } from 'client/util/Constants/ApplicationConstants';
import { mapGetters, mapState } from 'vuex';
import CustomApiAttributeMapping from 'client/applications/scim/customConnector/AttributeMappingSection.vue';
import EndpointConfigurationSection from 'client/applications/scim/customConnector/EndpointConfigurationSection.vue';
import GenericScimConnectorConfigurationHeader from 'client/applications/scim/GenericScimConnectorConfigurationHeader';
import GenericScimConnectorErrors from 'client/applications/scim/GenericScimConnectorErrors.vue';
import GenericScimConnectorGroups from 'client/applications/scim/GenericScimConnectorGroups.vue';
import GenericScimConnectorHeader from 'client/applications/scim/GenericScimConnectorHeader.vue';
import GenericScimConnectorValidation from 'client/applications/scim/GenericScimConnectorValidation.vue';
import IdmOauthConfigReloadModal from 'client/applications/scim/customConnector/IdmOauthConfigReloadModal.vue';
import IdmUsersImportFilterSection from 'client/applications/import/IdmUsersImportFilterSection';
import OAuthConfigurationSection from 'client/applications/scim/customConnector/OAuthConfigurationSection.vue';
import ScimConnectorAttributes from 'client/applications/scim/ScimConnectorAttributes';

const { application } = ItemTypeConsts;
const applicationModel = StoreModelMap[application];
const { OAUTH2 } = authenticationMethods;

export default {
  name: 'GenericScimConnectorConfig',

  components: {
    ScimConnectorAttributes,
    CustomApiAttributeMapping,
    EndpointConfigurationSection,
    GenericScimConnectorConfigurationHeader,
    GenericScimConnectorErrors,
    GenericScimConnectorHeader,
    GenericScimConnectorValidation,
    GenericScimConnectorGroups,
    IdmUsersImportFilterSection,
    OAuthConfigurationSection,
    IdmOauthConfigReloadModal,
  },

  inject: ['$validator'],

  props: {
    showOauthConfigReloadModal: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(applicationModel, {
      authenticationMethod: state => state.authenticationMethod,
      connectionType: state => state.scimConnectionType,
      isScimActive: state => state.isScimActive,
      scimIsTestConnectionPassed: state => state.scimIsTestConnectionPassed,
    }),

    ...mapGetters(applicationModel, ['didCustomApiConfigChanged']),

    areCredentialsValid() {
      const { flags } = this.$validator;
      const fields = Object.keys(flags);
      if (fields.length === 0) {
        return true;
      }

      return !fields.some(field => (flags[field].invalid
        || flags[field].invalid === null));
    },

    customApiFeatureFlag() {
      return this.$hasFeatureFlag(this.$featureFlags.importUserUpdatesCustomApi)
      && this.connectionType === scimConnectionTypes.CUSTOM;
    },

    isMethodOAuth2() {
      return this.authenticationMethod === OAUTH2;
    },

    showCustom() {
      return this.connectionType === scimConnectionTypes.CUSTOM;
    },
  },

  mounted() {
    this.registerIntersectionObserver();
    this.handleAutoScroll();
  },

  methods: {
    handleAutoScroll() {
      // For OAuth Custom REST APIs help user
      // auto-scroll to the correct section
      // Endpoint Configuration on successful auth
      // OAuth configuration section on failure of auth
      const isOauthIntegrationAuthorized = (this.isMethodOAuth2 && !this.isScimActive);

      const { customProtocolConfigRef, oauthConfigRef } = this.$refs;

      if (isOauthIntegrationAuthorized) {
        const scrollIntoViewConfig = { behavior: 'smooth', block: 'nearest', inline: 'nearest' };
        if (this.scimIsTestConnectionPassed) {
          customProtocolConfigRef.scrollIntoView(scrollIntoViewConfig);
        } else {
          oauthConfigRef.$el.scrollIntoView(scrollIntoViewConfig);
        }
      }
    },

    registerIntersectionObserver() {
    /**
     * Change style when element is "sticking"
     * @LINK https://davidwalsh.name/detect-sticky
     */
      const observer = new IntersectionObserver(
        ([e]) => e.target.classList.toggle(this.$style.stickyActive, e.intersectionRatio < 1),
        { threshold: [1] },
      );

      observer.observe(this.$refs.stickyHeader);
    },
  },
};
</script>

<style module>
@import '../../css/sections.css';

.scrollMargin {
  scroll-margin: 3rem;
}

.stickyActive {
  background-color: white;
  box-shadow: 0 5px 3px -3px rgba(0, 0, 0, 0.25);
  padding-top: 1rem;
}

.stickyHeader {
  position: -webkit-sticky;
  position: sticky;
  top: -1px;
  transition: padding-top 0.15s ease-in-out;
  z-index: 549;
}
</style>
